import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSession } from '../auth/session';
import type { ReglagesUrgence } from './urgence';

const CLE = ['reglages-notifications'];

/**
 * Les deux défenses du destinataire, lues et écrites côté serveur.
 *
 * Côté serveur et non dans le navigateur : un parent harcelé la nuit coupe l'urgence
 * depuis le téléphone qu'il a en main, et le réglage doit valoir pour l'ordinateur resté
 * allumé dans l'autre pièce — mais surtout pour la file de notifications, qui décide
 * seule si la sonnerie franchit ses heures silencieuses. Un réglage rangé dans le
 * navigateur n'aurait éteint que l'écran, jamais le téléphone.
 *
 * En cas d'échec de lecture, on suppose les deux défenses **actives** : c'est l'état par
 * défaut du produit, et supposer le contraire ferait taire une alerte à cause d'une
 * requête ratée.
 */
export function useReglagesUrgence() {
  const { client, profil } = useSession();
  const queryClient = useQueryClient();

  const reglages = useQuery({
    queryKey: CLE,
    enabled: profil !== null,
    staleTime: 5 * 60_000,
    queryFn: () => client.request<ReglagesUrgence>('/notifications/preferences'),
  });

  const enregistrer = useMutation({
    mutationFn: (patch: Partial<ReglagesUrgence>) =>
      client.request('/notifications/preferences', {
        method: 'PUT',
        // Le PUT remplace : on renvoie les réglages lus, corrigés du seul champ touché.
        // Envoyer le patch seul rendrait les heures silencieuses à leur défaut à chaque
        // fois qu'on coupe le son.
        body: { ...(reglages.data ?? {}), ...patch },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: CLE }),
  });

  return {
    son: reglages.data?.urgentSound ?? true,
    urgence: reglages.data?.urgentTakeover ?? true,
    charge: reglages.data !== undefined,
    enregistrer,
  };
}
